interface PitchedDropStepperProps {
  count: number;
  pending: boolean;
  onChange: (nextCount: number) => void;
  /** Accessible label suffix, e.g. "Berserked/Total Control drops for Aria at Lotus". */
  label: string;
}

const MAX_COUNT = 9999;

/**
 * REV17 (docs/specs/REV17-pitched-drop-counters.md) — a compact `n drops` +/− stepper. `PUT
 * /api/characters/{id}/pitched-drops/{bossSlug}` is an absolute set, so each click computes the
 * next value here and sends it whole (no server-side increment endpoint to race against).
 *
 * Used both as a `CollapsibleSection` `headerAction` (boss group headers already covered by a
 * Pitched item) and as a standalone row (box-only/Brilliant-only countable bosses with no group of
 * their own) — either way it must never toggle a collapsible section it happens to sit inside, so
 * every click stops propagation (REV2's "Complete all" header-action discipline, same idiom
 * `CharacterBossList` uses).
 */
export function PitchedDropStepper({ count, pending, onChange, label }: PitchedDropStepperProps) {
  return (
    <span className="flex shrink-0 items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        disabled={pending || count <= 0}
        onClick={(e) => {
          e.stopPropagation();
          onChange(count - 1);
        }}
        aria-label={`Decrease ${label}`}
        className="flex min-h-tap min-w-tap items-center justify-center rounded-full border border-border-strong bg-bg-elevated text-sm font-semibold leading-none text-text-faint transition-colors hover:bg-bg-elevated-hover disabled:cursor-not-allowed disabled:opacity-50"
      >
        &minus;
      </button>
      <span className="min-w-[3.75rem] whitespace-nowrap text-center text-xs font-medium text-text-faint">
        {count} {count === 1 ? 'drop' : 'drops'}
      </span>
      <button
        type="button"
        disabled={pending || count >= MAX_COUNT}
        onClick={(e) => {
          e.stopPropagation();
          onChange(count + 1);
        }}
        aria-label={`Increase ${label}`}
        className="flex min-h-tap min-w-tap items-center justify-center rounded-full border border-border-strong bg-bg-elevated text-sm font-semibold leading-none text-text-faint transition-colors hover:bg-bg-elevated-hover disabled:cursor-not-allowed disabled:opacity-50"
      >
        +
      </button>
    </span>
  );
}
